import {Board} from '@github-ui/board'
import {BoardColumnHeader, BoardRowHeader} from '@github-ui/board/frames'
import type {BoardColumn, BoardLabels, BoardRow} from '@github-ui/board-core'
import {Blankslate} from '@primer/react/experimental'
import type {ReactNode} from 'react'

import styles from './BoardSurface.module.css'
import type {BoardItem, BoardSource} from './to-board-data'
import {useBoardViewportHeight} from './use-board-viewport-height'

/** A swimlane's depth. Rows are a fixed height, sized by this rather than by their cards. */
const SWIMLANE_ROW_HEIGHT = '24rem'

/** A column's name and count, which is all a host with nothing richer to say needs. */
const renderPlainColumnHeader = (column: BoardColumn) => <BoardColumnHeader column={column} />

export type BoardSurfaceProps = {
  source: BoardSource
  renderCard: (item: BoardItem) => ReactNode
  /**
   * How a column names itself. Optional, because a column's name is the one thing this
   * layer can draw without knowing the domain, and a host that has richer material for
   * the field it grouped by -- a label's colour, a milestone's due date -- draws that
   * instead.
   */
  renderColumnHeader?: (column: BoardColumn) => ReactNode
  labels: BoardLabels
}

/**
 * The generic glue over `@github-ui/board`: it wires the mapped data and is agnostic about the
 * domain except for the `renderCard` and `renderColumnHeader` it is handed. A different host
 * reuses this unchanged and supplies its own source, card and header.
 *
 * The board owns its own scroll containers and cannot give itself a height: it fills its parent
 * and windows against that parent's height, so an unbounded parent makes it stretch the page and
 * its scrolling, virtualization and paging break together and silently. The host is sized to the
 * viewport at runtime (see `useBoardViewportHeight`) because the page layout above it renders a
 * header and sidebar and so cannot hand the board a bounded height in CSS alone.
 */
export function BoardSurface({source, renderCard, renderColumnHeader, labels}: BoardSurfaceProps) {
  const {data, columnField, rowField} = source
  const surfaceRef = useBoardViewportHeight()

  // D29: a board with no grouping has nothing to arrange into columns. That is an empty
  // state, not a fabricated single column.
  if (columnField == null) {
    return (
      <Blankslate>
        <Blankslate.Heading>Nothing to group by</Blankslate.Heading>
        <Blankslate.Description>Choose a field to group by to see this view as a board.</Blankslate.Description>
      </Blankslate>
    )
  }

  const swimlaned = rowField != null

  return (
    <div ref={surfaceRef} className={styles.boardSurface} data-testid="board-view">
      <Board<BoardItem>
        data={data}
        getItemId={item => item.globalId}
        getItemLabel={item => item.title}
        labels={labels}
        renderCard={renderCard}
        renderColumnHeader={renderColumnHeader ?? renderPlainColumnHeader}
        renderRowHeader={swimlaned ? (row: BoardRow) => <BoardRowHeader row={row} /> : undefined}
        rowHeight={swimlaned ? SWIMLANE_ROW_HEIGHT : undefined}
      />
    </div>
  )
}
